import React from "react";
import { Box, Flex, HStack } from "@chakra-ui/react";
import {
  BANNER_ITEM_HEIGHT,
  BannerEditorPictures,
  BannerEditorProps,
  MAX_BANNER_ITEMS,
  MAX_BANNER_WIDTH,
} from "./BannerEditor.types";
import { BannerEditoCard } from "./BannerEditorCard";
import { VeriDropZone } from "../../form-controls";
import {
  EditedPicture,
  PictureEditor,
  PictureToEdit,
} from "../../common/PictureEditor";
import { BannerEditorImagePreview } from "./BannerEditorImagePreview";
import { useVeriConfirmModal } from "../../modals/ConfirmModal/useVeriConfirmModal";
import { useBannerImageModel } from "../../../api/hooks/models/useBannerImageModel";
import { useVeriToast } from "../../../hooks";

export const BannerEditor: React.FC<BannerEditorProps> = ({
  onChange,
  images,
  layout,
  onLayoutChange,
  error,
  ...rest
}) => {
  const bannerImageModel = useBannerImageModel();
  const { toastError, toastServerError } = useVeriToast();

  const [_selectedLayout, setSelectedLayout] = React.useState(0);
  const [fileToEdit, setFileToEdit] = React.useState<
    PictureToEdit | undefined
  >();
  const [currentEditingImage, setCurrentEditingImage] = React.useState<
    number | undefined
  >();

  const [_bannerImages, setBannerImages] = React.useState<BannerEditorPictures>(
    [],
  );

  const [indexToDelete, setIndexToDelete] = React.useState<
    number | undefined
  >();

  const DeleteConfirmModal = useVeriConfirmModal({
    title: "Delete Image",
    message: "Are you sure you want to delete this image from your banner?",
    confirmLabel: "Yes, delete",
    cancelLabel: "No",
    onConfirm: () => {
      if (indexToDelete !== undefined) {
        setBannerImages((prev) => {
          const newImages = [...prev];
          newImages[indexToDelete] = null;
          onChange?.(newImages);
          return newImages;
        });
      }

      DeleteConfirmModal.close();
    },
  });

  const handleLayoutChange = (imagesNumber: number) => {
    setSelectedLayout(imagesNumber);
    const newImages = new Array(imagesNumber).fill(null);
    setBannerImages(newImages);
    onChange?.(newImages);
    onLayoutChange?.(imagesNumber);
  };

  const selectedLayout = layout !== undefined ? layout : _selectedLayout;
  const bannerImages = images !== undefined ? images : _bannerImages;

  React.useEffect(() => {
    setSelectedLayout(selectedLayout);
  }, [selectedLayout]);

  React.useEffect(() => {
    setBannerImages(bannerImages as never);
  }, [bannerImages]);

  return (
    <>
      <Box my={5}>
        You can create a customized banner using 1, 2, or 3 different images.
        Select layout:
      </Box>
      <Flex {...rest}>
        {selectedLayout > 0 ? (
          <Flex
            borderRadius={"4px"}
            border={"1px dashed"}
            borderColor={"border.grey"}
            w={"100%"}
            bg={"#F8F9FB"}
          >
            {new Array(selectedLayout).fill(null).map((_, index) => {
              const bannerImage = bannerImages[
                index
              ] as unknown as EditedPicture & PictureToEdit;

              return bannerImages[index] ? (
                <BannerEditorImagePreview
                  picture={{
                    url: bannerImage.croppedImage
                      ? URL.createObjectURL(bannerImage.croppedImage)
                      : bannerImage.previewUrl || bannerImage.url,
                  }}
                  onEdit={async () => {
                    if (bannerImage.id) {
                      try {
                        const image =
                          await bannerImageModel.getOriginalBannerImageDataUrl(
                            bannerImage.id,
                          );

                        if (!image) {
                          toastError("Error loading image");
                          return;
                        }

                        setFileToEdit({
                          file: bannerImage.originalFile,
                          url: image,
                          position: bannerImage.position,
                          scale: bannerImage.scale,
                          id: bannerImage.id,
                        });
                        setCurrentEditingImage(index);
                      } catch (err) {
                        toastServerError(err);
                      }

                      return;
                    }

                    setFileToEdit({
                      file: bannerImage.originalFile,
                      url: !bannerImage.originalFile ? bannerImage.url : "",
                      position: bannerImage.position,
                      scale: bannerImage.scale,
                    });
                    setCurrentEditingImage(index);
                  }}
                  onDelete={() => {
                    setIndexToDelete(index);
                    DeleteConfirmModal.open();
                  }}
                />
              ) : (
                <VeriDropZone
                  enableFileList={false}
                  multiple={false}
                  dropTypeText={"photo"}
                  acceptPreset={"image"}
                  height={`${BANNER_ITEM_HEIGHT}px`}
                  subDescription={`Minimum size: ${
                    MAX_BANNER_WIDTH / selectedLayout
                  } x ${BANNER_ITEM_HEIGHT} pixels`}
                  icon={
                    <Box fontSize={"100px"} color={"border.grey"}>
                      {index + 1}
                    </Box>
                  }
                  key={index}
                  onChange={(files) => {
                    setFileToEdit({
                      file: files[0],
                      url: "",
                    });
                    setCurrentEditingImage(index);
                  }}
                />
              );
            })}
          </Flex>
        ) : (
          <HStack w={"100%"} spacing={6}>
            {new Array(MAX_BANNER_ITEMS).fill(null).map((_, index) => (
              <BannerEditoCard
                onClick={handleLayoutChange.bind(null, index + 1)}
                key={index}
                imageCount={index + 1}
              />
            ))}
          </HStack>
        )}
      </Flex>
      {fileToEdit && (
        <PictureEditor
          cropWidth={MAX_BANNER_WIDTH / selectedLayout}
          cropHeight={BANNER_ITEM_HEIGHT}
          pictureToEdit={fileToEdit}
          isOpen
          onClose={setFileToEdit.bind(null, undefined)}
          onComplete={(editedFile) => {
            if (currentEditingImage !== undefined) {
              setBannerImages((prev) => {
                const newImages = [...prev];
                editedFile.itemRef = String(currentEditingImage);
                newImages[currentEditingImage] = editedFile;
                onChange?.(newImages);
                return newImages;
              });
            }

            setFileToEdit(undefined);
            setCurrentEditingImage(undefined);
          }}
        />
      )}
      {error && (
        <Box mt={6} color={"red.500"}>
          {error}
        </Box>
      )}
      <DeleteConfirmModal.Modal />
    </>
  );
};
